import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  useTheme,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Add, Edit, Delete, WarningAmber } from '@mui/icons-material';
import { flhaApi } from '../../services/api';
import { FLHAQuestion, Meta } from '../../types'; 
import { useNavigate } from 'react-router-dom';
import { extractApiError, unwrapListResponse, unwrapMetaResponse } from '../../apiUnwrapper/apiUnwrapper';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const FLHAPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageQuestions = user?.roleName === 'Admin';
  const [flhaQuestions, setFlhaQuestions] = useState<FLHAQuestion[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newOrder, setNewOrder] = useState<number>(0);
  const [newIsActive, setNewIsActive] = useState<boolean>(true);
  const [newIsRequired, setNewIsRequired] = useState<boolean>(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [flhaMeta, setFlhaMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState("Are you sure?");

  // Load questions from API
  useEffect(() => {
    loadQuestions(flhaMeta.page, flhaMeta.size);
  }, []);

  const loadQuestions = async (page = 1, size = 100) => {
    try {
      const data = await flhaApi.getQuestions(page, size); // implement getQuestions in your api
      setFlhaQuestions(unwrapListResponse<FLHAQuestion>(data));
      setFlhaMeta(unwrapMetaResponse<Meta>(data) || { total: 0, size: 5, page: 1 });
    } catch (err) {
      console.error('Error loading FLHA questions:', err);
    }
  };

  const openEditDialog = (q: FLHAQuestion) => {
    setEditId(q.id);
    setNewQuestion(q.question);
    setNewOrder(q.order);
    setNewIsActive(q.isActive);
    setNewIsRequired(q.isRequired);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    const payload: FLHAQuestion = {
      id: editId ?? 0,
      question: newQuestion,
      order: newOrder,
      isActive: newIsActive,
      isRequired: newIsRequired,
    };
    try {
      await flhaApi.createUpdateQuestion(payload);
      setOpenDialog(false);
      setEditId(null);
      setNewQuestion('');
      setNewOrder(0);
      setNewIsActive(true);
      setNewIsRequired(true);
      loadQuestions(flhaMeta.page, flhaMeta.size);
      enqueueSnackbar(editId ? 'FLHA question updated successfully' : 'FLHA question saved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving FLHA question:',err);
      enqueueSnackbar('Error saving FLHA question: '+ extractApiError(err).message, { variant: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    try{
            setConfirmAction(() => async () => {
            await flhaApi.deleteQuestion(id); // implement deleteQuestion in your api
            loadQuestions(flhaMeta.page, flhaMeta.size);
            enqueueSnackbar('FLHA question deleted successfully', { variant: 'success' });
          });
          setConfirmMessage("Are you sure you want to delete this question?");
          setOpenConfirm(true);
        }
        catch(error){
          console.error('Error deleting FLHA question:', error);
          enqueueSnackbar('Error deleting FLHA question: '+error, { variant: 'error' });
        }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
      <PageHeader
                                      title="SMART FLHA Configuration"
                                      Icon={WarningAmber}
                                      onBack={() => navigate('/prechecksandforms')}
                                      backLabel="Back to Pre Checks and Forms"
                                      subtitle=""
                                    />

        <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, mb: 3 }}>
          <CardContent>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, alignItems: 'start' }}>
            {canManageQuestions && <Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                }}
                onClick={() => setOpenDialog(true)}
              >
                Add FLHA Question
              </Button>
            </Box>}

            <Box sx={{ justifySelf: 'end', alignSelf: 'right' }}>
              <TablePagination
               component="div"
                count={flhaMeta.total}
                page={flhaMeta.page - 1}
                onPageChange={(e, newPage) => loadQuestions(newPage + 1, flhaMeta.size)}
                rowsPerPage={flhaMeta.size}
                onRowsPerPageChange={(e) => loadQuestions(1, parseInt(e.target.value, 10))}
                rowsPerPageOptions={[5, 10, 15, 20]}
                labelRowsPerPage="Questions per page"
              />
            </Box>
          </Box>


            <TableContainer
              component={Paper}
              sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2d3e50' }}>
                    <TableCell sx={{ color: theme.palette.text.primary }}>Order</TableCell>
                    <TableCell sx={{ color: theme.palette.text.primary }}>Question</TableCell>
                    <TableCell sx={{ color: theme.palette.text.primary }}>Status</TableCell>
                    <TableCell sx={{ color: theme.palette.text.primary }}>Required</TableCell>
                    {canManageQuestions && <TableCell sx={{ color: theme.palette.text.primary }} align="center">
                      Actions
                    </TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flhaQuestions.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell sx={{ color: theme.palette.text.primary }}>{q.order}</TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>{q.question}</TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {q.isActive ? <Chip label="Active" size="small" /> : <Chip label="Inactive" size="small" />}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>{q.isRequired ? 'Yes' : 'No'}</TableCell>
                       {canManageQuestions && <TableCell align="center">
                        <>
                          <IconButton sx={{ color: '#06beb6' }} onClick={() => openEditDialog(q)}>
                            <Edit />
                          </IconButton>
                          <IconButton sx={{ color: '#ef4444' }} onClick={() => handleDelete(q.id)}>
                            <Delete />
                          </IconButton>
                        </>
                       </TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                        component="div"
                        count={flhaMeta.total}
                        page={flhaMeta.page - 1}
                        onPageChange={(e, newPage) => loadQuestions(newPage + 1, flhaMeta.size)}
                        rowsPerPage={flhaMeta.size}
                        onRowsPerPageChange={(e) => loadQuestions(1, parseInt(e.target.value, 10))}
                        rowsPerPageOptions={[5, 10, 15, 20]}
                        labelRowsPerPage="Questions per page"
                      />
          <ConfirmDialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={confirmAction}
          title="Confirm Delete"
          message={confirmMessage}
        />
          </CardContent>
        </Card>
        
        {canManageQuestions && <Dialog open={openDialog} onClose={() => {
          setEditId(null);
          setNewQuestion('');
          setNewOrder(0);
          setNewIsActive(true);
          setNewIsRequired(true);
          setOpenDialog(false);
        }} fullWidth 
        maxWidth="md">
          <DialogTitle>Add FLHA Question</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="FLHA Question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion((e.target.value))}
                  fullWidth
                />
                <TextField
                  label="Order"
                  type="number"
                  value={newOrder}
                  onChange={(e) => setNewOrder(Number(e.target.value))}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox checked={newIsActive} onChange={(e) => setNewIsActive(e.target.checked)} />}
                  label="Active"
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel
                  control={<Checkbox checked={newIsRequired} onChange={(e) => setNewIsRequired(e.target.checked)} />}
                  label="Required"
                  sx={{ color: theme.palette.text.primary }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleSave} variant="contained" sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                }} autoFocus> {editId ? 'Update' : 'Save'}</Button>
            <Button onClick={() => {
              setEditId(null);
              setNewQuestion('');
              setNewOrder(0);
              setNewIsActive(true);
              setNewIsRequired(true);
              setOpenDialog(false);
            }} variant="contained" sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                }}>Cancel</Button>
          </DialogActions>
        </Dialog>}
      </Box>
    </Box>
  );
};

export default FLHAPage;